// apps/web/src/pages/pcp/remessas-estorno.tsx — Fase 17.1, Plano 28 (L-63, L-64, L-65, L-66).
// Estorno do recebimento pela lista de Remessas: o estorno é do DOCUMENTO FISCAL da NF recebida (não do id da remessa),
// só aparece para quem tem `remessas:estornar`, e a linha mostra o que já foi estornado.
// Ergonomia apenas: a autorização real é o 403 da API em `GET /remessas/estorno/impacto/:id` e `POST /remessas/estorno`.
import type { ReactElement } from 'react';

export const PERMISSAO_ESTORNAR_REMESSAS = 'remessas:estornar';

export const TEXTO_ESTORNO_COM_QUEM_TEM_PERMISSAO =
  'O estorno do recebimento é feito por quem tem a permissão de estornar remessas (Admin).';

export const TEXTO_ESTORNO_LINK_INVALIDO = 'O link do estorno não traz um documento fiscal válido.';

const REGEX_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A sessão pode estornar recebimento? Fail-closed enquanto a sessão não carrega. */
export function podeEstornarRemessas(permissoes: readonly string[] | null): boolean {
  return permissoes !== null && permissoes.includes(PERMISSAO_ESTORNAR_REMESSAS);
}

/** Rota da análise de impacto do estorno: o id é o do documento fiscal do recebimento (L-65). */
export function urlImpactoDoEstorno(documentoFiscalId: string): string {
  return `/api/v1/remessas/estorno/impacto/${encodeURIComponent(documentoFiscalId)}`;
}

export type EstornoDaUrl =
  | { readonly situacao: 'ausente' }
  | { readonly situacao: 'invalido' }
  | { readonly situacao: 'documento'; readonly documentoFiscalId: string };

/**
 * `?estornar=<documentoFiscalId>` (link da pendência CANCELAMENTO_NFE). O valor só é aceito se for um UUID; ele apenas
 * pré-seleciona o documento: o diálogo de impacto e a justificativa continuam obrigatórios (T-17.1-28-01/02).
 */
export function estornoDaUrl(search: string): EstornoDaUrl {
  const valor = new URLSearchParams(search).get('estornar');
  if (valor === null) return { situacao: 'ausente' };
  const limpo = valor.trim();
  return REGEX_UUID.test(limpo) ? { situacao: 'documento', documentoFiscalId: limpo.toLowerCase() } : { situacao: 'invalido' };
}

interface BotaoEstornarRemessaProps {
  /** Permissões da sessão; `null` enquanto a sessão carrega. */
  readonly permissoes: readonly string[] | null;
  /** Documento fiscal do recebimento da linha; `null` na remessa manual (sem NF, nada a estornar por aqui). */
  readonly documentoFiscalId: string | null;
  readonly estornada: boolean;
  readonly onEstornar: () => void;
}

/** Botão "Estornar" da linha: só para quem pode, só com NF e só enquanto houver o que estornar. */
export function BotaoEstornarRemessa({ permissoes, documentoFiscalId, estornada, onEstornar }: BotaoEstornarRemessaProps): ReactElement | null {
  if (!podeEstornarRemessas(permissoes) || documentoFiscalId === null || estornada) return null;
  return (
    <button
      type="button"
      onClick={onEstornar}
      className="text-xs font-medium text-rose-600 hover:text-rose-800 underline p-1.5 touch-manipulation"
    >
      Estornar
    </button>
  );
}

interface SeloEstornoProps {
  readonly estornada: boolean;
  readonly quantidadeEstornada: number;
}

/** Selo da linha: "Estornada" quando o estorno zerou o recebimento; "Estornado: N" no estorno parcial. */
export function SeloEstorno({ estornada, quantidadeEstornada }: SeloEstornoProps): ReactElement | null {
  if (estornada) {
    return (
      <span data-testid="selo-estornada" className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-rose-100 text-rose-800">
        Estornada
      </span>
    );
  }
  if (quantidadeEstornada > 0) {
    return (
      <span data-testid="selo-estorno-parcial" className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800">
        Estornado: {quantidadeEstornada}
      </span>
    );
  }
  return null;
}

interface AvisoEstornoDoLinkProps {
  readonly permissoes: readonly string[] | null;
  readonly estorno: EstornoDaUrl;
}

/** Orientação de quem chega por `?estornar=` sem poder estornar, ou com um link sem documento válido. */
export function AvisoEstornoDoLink({ permissoes, estorno }: AvisoEstornoDoLinkProps): ReactElement | null {
  if (estorno.situacao === 'ausente' || permissoes === null) return null;
  if (estorno.situacao === 'invalido') {
    return (
      <p data-testid="aviso-estorno-link-invalido" role="alert" className="p-3 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-md">
        {TEXTO_ESTORNO_LINK_INVALIDO}
      </p>
    );
  }
  if (podeEstornarRemessas(permissoes)) return null;
  return (
    <p data-testid="aviso-estorno-sem-permissao" role="status" className="p-3 bg-sky-50 border border-sky-200 text-sky-900 text-sm rounded-md">
      {TEXTO_ESTORNO_COM_QUEM_TEM_PERMISSAO}
    </p>
  );
}
